import React, { useLayoutEffect, useRef } from 'react';

import Box from 'carbon-react/lib/components/box';
import Button from 'carbon-react/lib/components/button';
import { Option, Select } from 'carbon-react/lib/components/select';
import { useNavigate, useParams } from 'react-router-dom';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { getReportingEditReportUrl } from '@futrli/shared-services';
import { useSidebarContext } from '@futrli/shared-services/contexts/sidebar-context/useSidebarContext';
import { UILoader } from '@futrli/ui-components';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';

import { ReportsContextProvider, useReportsContext } from '../../../../report-builder/reports.context';
import {
  ReportBuilderContextProvider,
  useReportBuilderContext
} from '../../../../report-builder/report-builder.context';
import { OnboardingContextProvider } from '../../../../report-builder/onboarding.context';
import { ReportIntlProvider } from '../../../../report-builder/components/report-intl-provider/report-intl-provider';
import { ReportRenderer } from '../../../../report-builder/components/report-renderer/report-renderer';
import { VirtualizedCardListEmptyState } from '../../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { usePerformanceDashboards } from './hooks/use-performance-dashboards';

import styles from './performance-dashboards-tab.scss';

const DASHBOARD_HORIZONTAL_PADDING = 128;

const DashboardContent = () => {
  const { reportViewRef } = useReportsContext();
  const { locale, setWorkAreaWidth } = useReportBuilderContext();
  const workAreaRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const element = workAreaRef.current;
    if (!element) {
      return;
    }

    const updateWorkAreaWidth = () => {
      setWorkAreaWidth((element.clientWidth || DASHBOARD_HORIZONTAL_PADDING) - DASHBOARD_HORIZONTAL_PADDING);
    };
    updateWorkAreaWidth();

    const resizeObserver = new ResizeObserver(updateWorkAreaWidth);
    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={workAreaRef} className={styles.dashboardArea}>
      <ReportIntlProvider locale={locale}>
        <ReportRenderer isDashboard isEditable={false} containerRef={reportViewRef} />
      </ReportIntlProvider>
    </div>
  );
};

/**
 * Main content for the Performance Dashboards tab.
 * Renders a page header, a Select dropdown for choosing a dashboard,
 * an Edit button to navigate to the reporting editor, and the dashboard
 * report rendered via ReportRenderer. Handles loading and empty states.
 */
const PerformanceDashboardsTabContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workspaceId = '', organisationId = '' } = useParams();
  const { dashboard } = useSidebarContext();
  const { patchReport } = useReportsContext();

  const {
    dashboards,
    selectedDashboardId,
    selectedDashboardDetail,
    flags: { canEditReport },
    isListLoading,
    isDetailLoading,
    setSelectedDashboardId
  } = usePerformanceDashboards();
  const canEditSelectedDashboard = canEditReport;

  const handleSelectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDashboardId(event.target.value);
  };

  const onEdit = () => {
    if (selectedDashboardId) {
      navigate(
        `${getReportingEditReportUrl(workspaceId, organisationId, selectedDashboardId)}?dash-id=${selectedDashboardId}`
      );
    }
  };

  return (
    <Box mt={3}>
      <ListingPageHeader
        variant="tab-header"
        title={intl.formatMessage({
          id: 'predict.dashboards.performance_dashboards',
          defaultMessage: 'Performance dashboards'
        })}
        helpUrl="https://support.futrli.com/en/articles/14597276-performance-dashboards"
      />

      {isListLoading ? (
        <UILoader isFull />
      ) : dashboards.length === 0 ? (
        <VirtualizedCardListEmptyState
          title={intl.formatMessage({ id: 'reporting.no_dashboards_to_show', defaultMessage: 'No dashboards to show' })}
          description={intl.formatMessage({
            id: 'reporting.first_dashboard_get_started',
            defaultMessage: 'Get started with your first dashboard'
          })}
          action={
            <Button buttonType="secondary" onClick={() => dashboard.open()}>
              <FormattedMessage id="reporting.create_dashboard" defaultMessage="Create dashboard" />
            </Button>
          }
        />
      ) : (
        <>
          <div className={styles.selectContainer}>
            <Box className={styles.selectDashboard}>
              <Select
                id="performance-dashboard-select"
                value={selectedDashboardId || ''}
                onChange={handleSelectChange}
                label={intl.formatMessage({ id: 'predict.select_dashboard', defaultMessage: 'Select dashboard' })}
              >
                {dashboards.map(d => (
                  <Option key={d.report_id} value={d.report_id} text={d.report_name} />
                ))}
              </Select>
            </Box>

            {isDetailLoading ? (
              <div className={styles.contentLoader} />
            ) : canEditSelectedDashboard ? (
              <Button buttonType="secondary" onClick={onEdit}>
                <FormattedMessage id="common.edit" defaultMessage="Edit" />
              </Button>
            ) : null}
          </div>

          {isDetailLoading ? (
            <div className={styles.spinnerContainer}>
              <UILoader isFull />
            </div>
          ) : selectedDashboardDetail ? (
            <ReportBuilderContextProvider
              key={selectedDashboardId}
              report={selectedDashboardDetail}
              patchReport={patchReport}
              viewPage
              isDashboardPage
            >
              <DashboardContent />
            </ReportBuilderContextProvider>
          ) : null}
        </>
      )}
    </Box>
  );
};

export const PerformanceDashboardsTab = () => {
  return (
    <ReportsContextProvider isInDashboardPage>
      <OnboardingContextProvider>
        <PerformanceDashboardsTabContent />
      </OnboardingContextProvider>
    </ReportsContextProvider>
  );
};
